import { useEffect, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Keyboard } from 'lucide-react';
import { useFocusTrap } from '../lib/useFocusTrap';

/* ── Shortcut Data ─────────────────────────────────────────────── */

interface Shortcut {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  title: string;
  shortcuts: Shortcut[];
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const mod = isMac ? '⌘' : 'Ctrl';

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: 'General',
    shortcuts: [
      { keys: [mod, 'K'], description: 'Open command palette' },
      { keys: ['?'], description: 'Show keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close dialogs & overlays' },
    ],
  },
  {
    title: 'Navigation',
    shortcuts: [
      { keys: [mod, '⇧', 'H'], description: 'Go to Dashboard' },
      { keys: [mod, '⇧', 'C'], description: 'Go to Calendar' },
      { keys: [mod, '⇧', 'P'], description: 'Go to Publish' },
      { keys: [mod, '⇧', 'A'], description: 'Go to Analytics' },
      { keys: [mod, '⇧', 'E'], description: 'Go to Engage' },
      { keys: [mod, '⇧', 'S'], description: 'Go to Settings' },
    ],
  },
  {
    title: 'Composer',
    shortcuts: [
      { keys: [mod, 'Enter'], description: 'Submit post' },
      { keys: [mod, 'S'], description: 'Save draft' },
    ],
  },
  {
    title: 'Engage',
    shortcuts: [
      { keys: ['Enter'], description: 'Send reply' },
    ],
  },
];

/* ── Hook ──────────────────────────────────────────────────────── */

export function useKeyboardShortcutsHelp() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Ignore when focus is in an input / textarea / contenteditable
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement).isContentEditable) {
        return;
      }

      if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { open, setOpen };
}

/* ── Component ─────────────────────────────────────────────────── */

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsHelp({ open, onClose }: KeyboardShortcutsHelpProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(open);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard shortcuts"
        >
          <motion.div
            ref={trapRef}
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="w-full max-w-lg rounded-2xl border border-white/30 bg-white/95 shadow-2xl backdrop-blur-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-4">
              <div className="flex items-center gap-2.5">
                <div className="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-pink-500 to-indigo-500">
                  <Keyboard className="size-4 text-white" />
                </div>
                <h2 className="text-sm font-semibold text-neutral-800">
                  Keyboard Shortcuts
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="size-4" />
              </button>
            </div>

            {/* Body */}
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              <div className="grid gap-6">
                {SHORTCUT_GROUPS.map((group) => (
                  <div key={group.title}>
                    <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                      {group.title}
                    </h3>
                    <div className="grid gap-1.5">
                      {group.shortcuts.map((s) => (
                        <div
                          key={s.description}
                          className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-neutral-50"
                        >
                          <span className="text-neutral-600">{s.description}</span>
                          <div className="flex items-center gap-1">
                            {s.keys.map((key, i) => (
                              <span key={i}>
                                <kbd className="inline-flex min-w-[22px] items-center justify-center rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[11px] font-medium text-neutral-500">
                                  {key}
                                </kbd>
                                {i < s.keys.length - 1 && (
                                  <span className="mx-0.5 text-[10px] text-neutral-300">+</span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-100 px-5 py-3">
              <p className="text-center text-[11px] text-neutral-400">
                Press <kbd className="rounded border border-neutral-200 bg-neutral-50 px-1 py-0.5 text-[10px] font-medium">?</kbd> to toggle this help
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
